import React, { useState, useEffect, useCallback } from 'react';
import { useInventory } from '../../../contexts/InventoryContext';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../Common/LoadingSpinner';
import TextInput from '../../Common/TextInput';
import CustomSelect from '../../Common/CustomSelect';
import PermissionGuard from '../../Common/PermissionGuard';
import SupplierForm from './SupplierForm';
import SupplierBulkUpload from './SupplierBulkUpload';
import SupplierBulkUpdatePhone from './SupplierBulkUpdatePhone';
import inventoryApi from '../../../services/inventoryApi';

const SupplierManagement = () => {
  // This page fetches its own page-by-page data directly via inventoryApi (like the Finance module's
  // Banks/JournalEntry pages) rather than the shared InventoryContext list, which is used elsewhere
  // for "give me everything" cases (e.g. supplier dropdowns). deleteSupplier still goes through the
  // context so other consumers of it stay in sync.
  const { deleteSupplier } = useInventory();

  const { hasPermission } = useAuth();

  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem('supplierManagementView') || 'card';
    } catch (error) {
      return 'card';
    }
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showBulkUpdatePhone, setShowBulkUpdatePhone] = useState(false);
  const [retryingLedger, setRetryingLedger] = useState(null);

  // Page-by-page server data (mirrors client/src/components/Finance/Banks/Banks.jsx)
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });

  // Debounce search so every keystroke doesn't trigger a fetch
  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const loadSuppliers = useCallback(async () => {
    try {
      setLoading(true);

      const params = { page: pagination.page, limit: pagination.limit };
      if (debouncedSearchTerm.trim()) params.search = debouncedSearchTerm.trim();
      // Status defaults to isActive=true on the backend unless explicitly told otherwise.
      if (statusFilter === 'active') params.isActive = 'true';
      else if (statusFilter === 'inactive') params.isActive = 'false';
      else params.isActive = 'all';
      if (typeFilter !== 'all') params.supplierType = typeFilter;

      const body = await inventoryApi.getSuppliers(params);
      const list = Array.isArray(body.data) ? body.data : [];
      setSuppliers(list);
      setPagination(prev => ({ ...prev, ...(body.pagination || {}) }));
      setFetchError(null);
    } catch (err) {
      console.error('Error loading suppliers:', err);
      setFetchError(err.response?.data?.message || err.message || 'Failed to load suppliers');
      setSuppliers([]);
      setPagination(prev => ({ ...prev, total: 0, pages: 1 }));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, debouncedSearchTerm, statusFilter, typeFilter]);

  // Reset to page 1 whenever a server-side filter changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [debouncedSearchTerm, statusFilter, typeFilter]);

  useEffect(() => {
    // Check read permission before making API call
    if (hasPermission('INVENTORY:SUPPLIERS:READ')) {
      loadSuppliers();
    }
  }, [loadSuppliers, hasPermission]);

  const handleViewChange = (newView) => {
    setView(newView);
    try {
      localStorage.setItem('supplierManagementView', newView);
    } catch (error) {
      console.warn('Failed to save view preference');
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
  };

  const handlePriorityFilter = (e) => {
    setPriorityFilter(e.target.value);
  };

  const handleTypeFilter = (e) => {
    setTypeFilter(e.target.value);
  };

  const handleCreateSupplier = () => {
    if (!hasPermission('INVENTORY:SUPPLIERS:CREATE')) {
      alert('You do not have permission to create suppliers');
      return;
    }
    setEditingSupplier(null);
    setShowForm(true);
  };

  const handleEditSupplier = (supplier) => {
    if (!hasPermission('INVENTORY:SUPPLIERS:UPDATE')) {
      alert('You do not have permission to edit suppliers');
      return;
    }
    setEditingSupplier(supplier);
    setShowForm(true);
  };

  const handleDeleteSupplier = async (supplier) => {
    if (!hasPermission('INVENTORY:SUPPLIERS:DELETE')) {
      alert('You do not have permission to delete suppliers');
      return;
    }

    try {
      await deleteSupplier(supplier._id);
      setShowDeleteConfirm(null);
      await loadSuppliers();
    } catch (err) {
      console.error('Failed to delete supplier:', err);
      setFetchError(err.response?.data?.message || err.message || 'Failed to delete supplier');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingSupplier(null);
    loadSuppliers();
  };

  const handleRetryLedger = async (supplierId) => {
    setRetryingLedger(supplierId);
    try {
      await inventoryApi.retryLedgerAccount(supplierId);
      await loadSuppliers();
    } catch (error) {
      console.error('Failed to create ledger account:', error);
    } finally {
      setRetryingLedger(null);
    }
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">Active</span>;
    }
    return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">Inactive</span>;
  };

  const getPriorityBadge = (isPrimary) => {
    if (isPrimary) {
      return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">Primary</span>;
    }
    return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">Secondary</span>;
  };

  const getRatingStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={`text-sm ${i <= rating ? 'text-yellow-400' : 'text-gray-300'}`}>
          ★
        </span>
      );
    }
    return <div className="flex items-center">{stars}</div>;
  };

  const getSupplierIcon = (supplier) => {
    return supplier.name?.substring(0, 2).toUpperCase() || 'SU';
  };

  const renderSupplierCard = (supplier) => {
    return (
      <div key={supplier._id} className="bg-white border border-gray-200 rounded shadow-sm hover:shadow-md transition-all duration-200 group h-36 w-full">
        <div className="p-3 h-full flex flex-col">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-xs font-semibold text-indigo-700 flex-shrink-0">
                {getSupplierIcon(supplier)}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-gray-900 truncate">
                  {supplier.name}
                </h3>
                <p className="text-xs text-gray-500 truncate">{supplier.code || `SUP-${supplier._id?.slice(-6)?.toUpperCase()}`}</p>
              </div>
            </div>

            <div className="flex items-center space-x-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
              <PermissionGuard permission="INVENTORY:SUPPLIERS:UPDATE">
                <button
                  onClick={() => handleEditSupplier(supplier)}
                  className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                  title="Edit supplier"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              </PermissionGuard>

              <PermissionGuard permission="INVENTORY:SUPPLIERS:DELETE">
                <button
                  onClick={() => setShowDeleteConfirm(supplier)}
                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                  title="Delete supplier"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </PermissionGuard>
            </div>
          </div>

          {supplier.contactInfo?.contactPerson && (
            <p className="text-xs text-gray-600 mb-2 line-clamp-2 leading-tight flex-1">
              Contact: {supplier.contactInfo.contactPerson}
            </p>
          )}

          <div className="flex flex-wrap gap-1 mt-auto">
            {getStatusBadge(supplier.isActive)}
            {getPriorityBadge(supplier.isPrimary)}
            {supplier.performance?.rating && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
                ★ {supplier.performance.rating}
              </span>
            )}
            {supplier.ledgerAccount?.syncStatus === 'synced' && supplier.ledgerAccount?.accountId ? (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200" title={supplier.ledgerAccount.accountName}>
                ✓ {supplier.ledgerAccount.accountCode}
              </span>
            ) : (
              <button
                onClick={() => handleRetryLedger(supplier._id)}
                disabled={retryingLedger === supplier._id}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors disabled:opacity-50"
                title="Create ledger account in finance"
              >
                {retryingLedger === supplier._id ? (
                  <svg className="animate-spin w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                ) : '⚠ '}
                No COA
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSupplierRow = (supplier) => {
    return (
      <tr key={supplier._id} className="hover:bg-gray-50 transition-colors">
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-sm font-semibold text-indigo-700">
              {getSupplierIcon(supplier)}
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900">{supplier.name}</div>
              <div className="text-xs text-gray-500">{supplier.code || `SUP-${supplier._id?.slice(-6)?.toUpperCase()}`}</div>
            </div>
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            supplier.supplierType === 'expense' 
              ? 'bg-orange-100 text-orange-800' 
              : 'bg-blue-100 text-blue-800'
          }`}>
            {supplier.supplierType === 'expense' ? (
              <>
                <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Expense
              </>
            ) : (
              <>
                <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                Trade
              </>
            )}
          </span>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="text-sm">
            {supplier.contactInfo?.contactPerson && (
              <div className="font-medium text-gray-900">{supplier.contactInfo.contactPerson}</div>
            )}
            {supplier.contactInfo?.email && (
              <div className="text-gray-600">{supplier.contactInfo.email}</div>
            )}
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-center">
          {supplier.performance?.rating && (
            <div className="flex items-center justify-center space-x-1">
              {getRatingStars(supplier.performance.rating)}
              <span className="text-gray-600 ml-1">({supplier.performance.rating})</span>
            </div>
          )}
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-center">
          <div className="space-y-1">
            {getStatusBadge(supplier.isActive)}
            {getPriorityBadge(supplier.isPrimary)}
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          {supplier.ledgerAccount?.syncStatus === 'synced' && supplier.ledgerAccount?.accountId ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" title={supplier.ledgerAccount.accountName}>
              ✓ {supplier.ledgerAccount.accountCode}
            </span>
          ) : (
            <button
              onClick={() => handleRetryLedger(supplier._id)}
              disabled={retryingLedger === supplier._id}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors disabled:opacity-50"
              title="Create ledger account in finance service"
            >
              {retryingLedger === supplier._id ? (
                <svg className="animate-spin w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
              ) : <span className="mr-1">⚠</span>}
              Create COA
            </button>
          )}
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex items-center justify-end space-x-2">
            <PermissionGuard permission="INVENTORY:SUPPLIERS:UPDATE">
              <button
                onClick={() => handleEditSupplier(supplier)}
                className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all"
                title="Edit supplier"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            </PermissionGuard>

            <PermissionGuard permission="INVENTORY:SUPPLIERS:DELETE">
              <button
                onClick={() => setShowDeleteConfirm(supplier)}
                className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-all"
                title="Delete supplier"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </PermissionGuard>
          </div>
        </td>
      </tr>
    );
  };

  // search/status/supplierType are already applied server-side (see loadSuppliers above). priorityFilter
  // has no backing field on the Supplier model (no `isPrimary`) — kept as a harmless client-side
  // no-op filter over just the current page, matching its pre-existing (already non-functional) behavior.
  const displaySuppliers = suppliers.filter(supplier => {
    return priorityFilter === 'all' ||
      (priorityFilter === 'primary' && supplier.isPrimary) ||
      (priorityFilter === 'secondary' && !supplier.isPrimary);
  });

  // Pagination handlers — trigger a re-fetch of that page via loadSuppliers (see useEffect above)
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleItemsPerPageChange = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  // Windowed page-number buttons (1 ... current-1 current current+1 ... last), matching
  // client/src/components/Finance/Banks/Banks.jsx's getPaginationButtons()
  const getPaginationButtons = () => {
    const buttons = [];
    const totalPages = pagination.pages || 1;
    const currentPage = pagination.page || 1;

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) buttons.push(i);
    } else {
      buttons.push(1);
      if (currentPage > 3) buttons.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) buttons.push(i);
      if (currentPage < totalPages - 2) buttons.push('...');
      if (totalPages > 1) buttons.push(totalPages);
    }
    return buttons;
  };

  if (showForm) {
    return (
      <SupplierForm
        supplier={editingSupplier}
        onSuccess={handleFormSuccess}
        onCancel={() => {
          setShowForm(false);
          setEditingSupplier(null);
        }}
      />
    );
  }

  if (loading && suppliers.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <PermissionGuard
      permission="INVENTORY:SUPPLIERS:READ"
      fallback={
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 0h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
            <p className="mt-1 text-sm text-gray-500">You don't have permission to view suppliers.</p>
          </div>
        </div>
      }
    >
      <div className="bg-white shadow-sm border border-gray-200">
          {/* Error Display */}
          {fetchError && (
            <div className="mb-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded p-3">
              <div className="flex items-center">
                <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium text-red-800">{fetchError}</p>
                <button
                  onClick={() => setFetchError(null)}
                  className="ml-auto text-red-600 hover:text-red-500"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Form Modal */}
          {showForm && (
            <SupplierForm
              supplier={editingSupplier}
              onSuccess={handleFormSuccess}
              onCancel={() => {
                setShowForm(false);
                setEditingSupplier(null);
              }}
            />
          )}

          {/* Main Content */}
          {!showForm && (
            <div className="bg-white shadow-sm border border-gray-200">
              {/* Filters and Search */}
              <div className="px-4 sm:px-6 lg:px-8 py-2 border-b border-gray-200 flex-shrink-0 bg-gray-50">
                <div className="flex flex-col lg:flex-row lg:items-center gap-2">
                  {/* Search */}
                  <div className="flex-1">
                    <TextInput
                      variant="input"
                      type="text"
                      placeholder="Search suppliers by name, code, or contact..."
                      value={searchTerm}
                      onChange={handleSearch}
                      icon={({ className }) => (
                        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      )}
                      className="text-sm py-1.5 !bg-white border border-gray-300 shadow-sm !rounded-sm"
                    />
                  </div>

                  {/* Filters and Actions */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    {/* Status Filter */}
                    <CustomSelect
                      value={statusFilter}
                      onChange={handleStatusFilter}
                      options={[
                        { value: 'all', label: 'All Status' },
                        { value: 'active', label: 'Active' },
                        { value: 'inactive', label: 'Inactive' }
                      ]}
                      size="sm"
                      variant="default"
                      className="min-w-[120px]"
                    />

                    {/* Priority Filter */}
                    <CustomSelect
                      value={priorityFilter}
                      onChange={handlePriorityFilter}
                      options={[
                        { value: 'all', label: 'All Types' },
                        { value: 'primary', label: 'Primary' },
                        { value: 'secondary', label: 'Secondary' }
                      ]}
                      size="sm"
                      variant="default"
                      className="min-w-[120px]"
                    />

                    {/* Supplier Type Filter */}
                    <CustomSelect
                      value={typeFilter}
                      onChange={handleTypeFilter}
                      options={[
                        { value: 'all', label: 'All Suppliers' },
                        { value: 'trade', label: 'Trade' },
                        { value: 'expense', label: 'Expense' }
                      ]}
                      size="sm"
                      variant="default"
                      className="min-w-[130px]"
                    />

                    {/* View Toggle */}
                    <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5">
                      <button
                        onClick={() => handleViewChange('card')}
                        className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'card'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                          }`}
                        title="Card view"
                      >
                        <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-7H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z" />
                        </svg>
                        Cards
                      </button>
                      <button
                        onClick={() => handleViewChange('list')}
                        className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'list'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                          }`}
                        title="List view"
                      >
                        <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                        List
                      </button>
                    </div>

                    {/* Bulk Update Phone Numbers Button */}
                    <PermissionGuard permission="INVENTORY:SUPPLIERS:UPDATE">
                      <button
                        type="button"
                        onClick={() => setShowBulkUpdatePhone(true)}
                        className="inline-flex items-center px-3 py-1.5 bg-white border border-indigo-200 text-indigo-700 text-sm font-medium rounded-md shadow-sm hover:bg-indigo-50 transition-all duration-200"
                      >
                        <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m-4.5 4.5L7 12l-2.5 2.5M13 19h8m-4-4l4 4-4 4" />
                        </svg>
                        Bulk update phone numbers
                      </button>
                    </PermissionGuard>

                    {/* Add Supplier Button */}
                    <PermissionGuard permission="INVENTORY:SUPPLIERS:CREATE">
                      <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowBulkUpload(true)}
                        className="inline-flex items-center px-3 py-1.5 bg-white border border-indigo-200 text-indigo-700 text-sm font-medium rounded-md shadow-sm hover:bg-indigo-50 transition-all duration-200"
                      >
                        <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Bulk upload
                      </button>
                      <button
                        onClick={handleCreateSupplier}
                        className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200"
                      >
                        <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Supplier
                      </button>
                      </div>
                    </PermissionGuard>
                  </div>
                </div>
              </div>
              {/* Pagination */}
              {pagination.total > 0 && (
                <div className="flex items-center justify-between pb-2 border-b  px-2  py-2 bg-gradient-to-r from-gray-100 to-gray-100 mb-6 border-b border-gray-200">
                  {/* Results Info */}
                  <div className="flex items-center gap-2">
                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-1.5 rounded-lg shadow-sm">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        <span>
                          {((pagination.page - 1) * pagination.limit) + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} suppliers
                        </span>
                        {loading && (
                          <svg className="animate-spin h-3 w-3 text-indigo-500" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all' || typeFilter !== 'all' ? 'Filtered results' : 'All suppliers'}
                      </p>
                    </div>
                  </div>

                  {/* Navigation Controls */}
                  <div className="flex items-center gap-3">
                    {/* Items per page */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 font-medium">Show:</span>
                      <CustomSelect
                        value={pagination.limit}
                        onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                        disabled={loading}
                        options={[
                          { value: 10, label: '10' },
                          { value: 20, label: '20' },
                          { value: 50, label: '50' },
                          { value: 100, label: '100' }
                        ]}
                        size="sm"
                        variant="minimal"
                        className="min-w-[70px]"
                      />
                    </div>

                    {/* Navigation Buttons */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handlePageChange(1)}
                        disabled={pagination.page <= 1 || loading}
                        className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        title="First page"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                        </svg>
                      </button>

                      <button
                        onClick={() => handlePageChange(Math.max(1, pagination.page - 1))}
                        disabled={pagination.page <= 1 || loading}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        <span className="hidden sm:inline">Previous</span>
                      </button>

                      {/* Page Numbers */}
                      <div className="hidden md:flex items-center gap-1">
                        {getPaginationButtons().map((button, index) => (
                          <button
                            key={index}
                            onClick={() => typeof button === 'number' && handlePageChange(button)}
                            disabled={button === '...' || loading}
                            className={`min-w-[32px] px-2.5 py-1.5 rounded text-sm font-medium transition-all ${button === pagination.page
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : button === '...'
                                ? 'text-gray-400 cursor-default'
                                : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                              }`}
                          >
                            {button}
                          </button>
                        ))}
                      </div>

                      {/* Mobile page indicator */}
                      <div className="flex md:hidden px-3 py-1.5 bg-white rounded border border-gray-200">
                        <span className="text-sm font-medium text-gray-900">{pagination.page}</span>
                        <span className="text-sm text-gray-500 mx-1">of</span>
                        <span className="text-sm font-medium text-gray-900">{pagination.pages}</span>
                      </div>

                      <button
                        onClick={() => handlePageChange(Math.min(pagination.pages, pagination.page + 1))}
                        disabled={pagination.page >= pagination.pages || loading}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                      >
                        <span className="hidden sm:inline">Next</span>
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>

                      <button
                        onClick={() => handlePageChange(pagination.pages)}
                        disabled={pagination.page >= pagination.pages || loading}
                        className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        title="Last page"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Content */}
              <div className="p-2">
                {displaySuppliers.length === 0 ? (
                  <div className="text-center py-12">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No suppliers found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all' || typeFilter !== 'all' ?
                        'Try adjusting your search or filter criteria.' :
                        'Get started by creating your first supplier.'
                      }
                    </p>
                    {!searchTerm && statusFilter === 'all' && priorityFilter === 'all' && typeFilter === 'all' && (
                      <div className="mt-6">
                        <PermissionGuard permission="INVENTORY:SUPPLIERS:CREATE">
                          <button
                            onClick={handleCreateSupplier}
                            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Create Supplier
                          </button>
                        </PermissionGuard>
                      </div>
                    )}
                  </div>
                ) : (
                  <>

                    {/* Card View */}
                    {view === 'card' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {displaySuppliers.map(renderSupplierCard)}
                      </div>
                    )}

                    {/* List View */}
                    {view === 'list' && (
                      <div className="overflow-x-auto">
                        <table className="min-w-max w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Supplier
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Type
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Contact Info
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Rating
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Ledger Account
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {displaySuppliers.map(renderSupplierRow)}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Bulk upload modal */}
          {showBulkUpload && (
            <SupplierBulkUpload
              onSuccess={() => {
                setShowBulkUpload(false);
                loadSuppliers();
              }}
              onCancel={() => setShowBulkUpload(false)}
            />
          )}

          {showBulkUpdatePhone && (
            <SupplierBulkUpdatePhone
              onSuccess={() => {
                setShowBulkUpdatePhone(false);
                loadSuppliers();
              }}
              onCancel={() => setShowBulkUpdatePhone(false)}
            />
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
              <div className="relative bg-white rounded shadow-2xl border border-gray-100 w-full max-w-md">
                <div className="p-6">
                  <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded mb-4">
                    <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>

                  <div className="text-center mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Supplier</h3>
                    <p className="text-sm text-gray-600">
                      Are you sure you want to delete <span className="font-medium text-gray-900">"{showDeleteConfirm.name}"</span>?
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      This action cannot be undone.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDeleteConfirm(null)}
                      className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium rounded transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleDeleteSupplier(showDeleteConfirm)}
                      className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded transition-colors"
                    >
                      Delete Supplier
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
    </PermissionGuard>
  );
};

export default SupplierManagement;
